"use strict";

/**
 * CSRF freshness + bfcache recovery module.
 *
 * Problem: auth forms render a static @csrf token at page load. If the page
 * sits idle past SESSION_LIFETIME, or is restored from the browser back/forward
 * cache (bfcache), the baked token is stale and the POST returns 419. Password
 * managers like 1Password autofill + autosubmit on restored pages, reliably
 * triggering this failure.
 *
 * Strategy:
 * - On pageshow with event.persisted (bfcache restore) -> hard reload so the
 *   server re-renders a fresh page with a fresh token.
 * - Before submitting any form with data-refresh-csrf, fetch a fresh token and
 *   patch it into every input[name="_token"] and the meta tag.
 * - Expose refreshCsrfToken() for the axios 419 interceptor to reuse.
 */

/**
 * Fetch a fresh CSRF token from the server and apply it everywhere on the page.
 *
 * @returns {Promise<string>} The new token value.
 */
async function refreshCsrfToken() {
    const res = await fetch("/csrf-token", {
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
        },
        credentials: "same-origin",
        cache: "no-store",
    });

    if (!res.ok) {
        throw new Error("CSRF refresh failed: " + res.status);
    }

    const { token } = await res.json();
    applyCsrfToken(token);

    return token;
}

/**
 * Write a CSRF token value into all page locations that need it:
 * - The <meta name="csrf-token"> tag (read by axios at load time).
 * - The window.axios default header (for XHR/fetch via axios).
 * - Every hidden input[name="_token"] (for plain HTML form POSTs).
 *
 * @param {string} token
 */
function applyCsrfToken(token) {
    const meta = document.head.querySelector('meta[name="csrf-token"]');
    if (meta) {
        meta.setAttribute("content", token);
    }

    if (window.axios) {
        window.axios.defaults.headers.common["X-CSRF-TOKEN"] = token;
    }

    document
        .querySelectorAll('input[name="_token"]')
        .forEach((input) => (input.value = token));
}

// Expose helpers so the axios 419 interceptor (in axios.js) can call them.
window.refreshCsrfToken = refreshCsrfToken;
window.applyCsrfToken = applyCsrfToken;

// bfcache restore defense: reload the page so the server renders a fresh token.
// This also prevents 1Password from autofilling + submitting a stale-token page
// that was cached by the browser back/forward mechanism.
window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
        window.location.reload();
    }
});

// Pre-submit token refresh: intercept form submissions on forms that opt in via
// data-refresh-csrf attribute. Fetch a fresh token, apply it, then re-submit.
// The guard flag data-csrf-refreshed prevents an infinite retry loop.
document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) {
        return;
    }
    if (!form.hasAttribute("data-refresh-csrf")) {
        return;
    }
    if (form.dataset.csrfRefreshed === "1") {
        // Already refreshed on this submit attempt - let it through.
        return;
    }

    event.preventDefault();

    try {
        await refreshCsrfToken();
    } catch (e) {
        // Refresh failed (e.g. offline). Fall through and submit with the
        // existing token; the graceful 419 handler in bootstrap/app.php will
        // redirect back with a retry message rather than a dead-end page.
        console.warn("CSRF token refresh failed, submitting with existing token.", e);
    }

    form.dataset.csrfRefreshed = "1";
    form.submit();
});
